
import { useState } from "react";
import { BookingFormData } from "../ParcelBookingForm";
import { useGoogleMapsKey } from "@/hooks/useGoogleMapsKey";
import { AddressSection } from "../address-form/AddressSection";
import { useAddressValidation } from "@/hooks/address/useAddressValidation";
import { useAddressHandlers } from "@/hooks/address/useAddressHandlers";
import { useAddressPlaceHandlers } from "@/hooks/address/useAddressPlaceHandlers";
import { Info, Shield } from "lucide-react";
import { LegalContentDialog } from "@/components/legal/LegalContentDialog";

interface AddressStepProps {
  formData: BookingFormData;
  setFormData: React.Dispatch<React.SetStateAction<BookingFormData>>;
  onValidationChange?: (isValid: boolean) => void;
}

const AddressStep = ({ formData, setFormData, onValidationChange }: AddressStepProps) => {
  const { apiKey, error, isLoading } = useGoogleMapsKey();
  const [showLegalDialog, setShowLegalDialog] = useState(false);

  const { validateAddresses } = useAddressValidation({
    collectionAddress: formData.collectionAddress,
    deliveryAddress: formData.deliveryAddress,
    onValidationChange,
  });

  const { handleCollectionChange, handleDeliveryChange } = useAddressHandlers({
    formData,
    setFormData,
    validateAddresses,
  });

  const { handleCollectionSelect, handleDeliverySelect } = useAddressPlaceHandlers({
    formData,
    setFormData,
    validateAddresses,
  });

  if (isLoading) {
    return <div className="flex items-center justify-center p-4">Loading address search...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!apiKey) {
    return <div className="text-red-500 p-4">Unable to load address search. Please check your configuration.</div>;
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Instructional Banner */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border">
        <Info className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          Please review address details to ensure they're correct. Town and suburb may require verification when parcel is scheduled for collection.
        </p>
      </div>

      <AddressSection
        title="Collection Address"
        values={formData.collectionAddress}
        onChange={handleCollectionChange}
        onPlaceSelect={handleCollectionSelect}
        prefix="collection"
      />
      <AddressSection
        title="Delivery Address"
        values={formData.deliveryAddress}
        onChange={handleDeliveryChange}
        onPlaceSelect={handleDeliverySelect}
        prefix="delivery"
      />

      {/* Privacy Policy Footer */}
      <div className="flex items-center justify-center gap-2 pt-2 text-center">
        <Shield className="h-4 w-4 text-muted-foreground" />
        <button 
          type="button"
          onClick={() => setShowLegalDialog(true)}
          className="text-sm text-muted-foreground hover:text-primary hover:underline transition-colors"
        >
          Review our Privacy Policy for details on protection of personal information.
        </button>
      </div>

      {/* Legal Content Dialog */}
      <LegalContentDialog 
        open={showLegalDialog} 
        onOpenChange={setShowLegalDialog}
        initialTab="privacy"
      />
    </div>
  );
};

export default AddressStep;
